(() => {
  const storageKey = '__svea_widget_logs_v1';
  if (window.__sveaWidgetGlobalHooksReady === true) return;
  const isDesignMode = !!(window.Shopify && window.Shopify.designMode === true);
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('sveaDebug') === '1') {
      window.__sveaWidgetVerboseLogs = true;
    }
    if (params.get('pdpDebug') === '1' || params.get('variantDebug') === '1') {
      window.__variantSelectionVerboseLogs = true;
    }
  } catch (e) {}
  if (isDesignMode) {
    window.__variantSelectionVerboseLogs = true;
  }

  window.getSveaWidgetLogs = window.getSveaWidgetLogs || (() => {
    try {
      const raw = window.sessionStorage.getItem(storageKey) || '[]';
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  });

  window.clearSveaWidgetLogs = window.clearSveaWidgetLogs || (() => {
    try {
      window.sessionStorage.removeItem(storageKey);
    } catch (e) {}
    window.__sveaWidgetLogs = [];
    return [];
  });

  window.__sveaWidgetGlobalHooksReady = true;
})();

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.userHasInteractedWithVariant = false;
    this.addEventListener('change', this.onVariantChange);
  }

  connectedCallback() {
    const pendingRestore = this.restorePendingCombinedSelectionState();

    this.updateOptions();
    this.updateMasterId();
    this.updateVariantStatuses();
    this.updateMedia();
    this.updateDimensions();
    if (pendingRestore?.appliedCount > 0) {
      this.applyVariantSelectionState('pending-navigation-restore');
    }
    this.pruneEmptyProductBlocks();
    this.bindSniffieOmnibusGuards();
    this.bindSelectionPersistenceOnLinks();
    this.bindSveaWidgetGuards();
    this.bindSveaWidgetInteractions();
    this.bindSveaQuantityWatchers();
    if (!(pendingRestore?.appliedCount > 0)) {
      this.updateSveaWidget();
    }

    this.variantLog('picker.connected', {
      sectionId: this.dataset.section || null,
      currentVariantId: this.currentVariant?.id || null,
      pendingRestore,
      selections: this.captureSelectedOptions()
    });
  }

  onVariantChange(e) {
    if (e?.isTrusted) {
      this.userHasInteractedWithVariant = true;
    }

    const inputTarget = e?.target || null;
    const selectionsBeforeChange = this.captureSelectedOptions();
    this.sveaLog('info', 'variant.change.received', {
      inputName: inputTarget?.name || null,
      inputValue: inputTarget?.value || null,
      inputType: inputTarget?.type || null,
      targetProductUrl: inputTarget?.dataset?.productUrl || null
    });

    const { isCombinedProduct, targetUrl } = this.combinedListingCheck(e);

    this.toggleAddButton(true, '', false);
    if (typeof this.hideUnavailableMessage === 'function') {
      this.hideUnavailableMessage();
    }

    this.variantLog('variant.change.input', {
      inputName: inputTarget?.name || null,
      inputValue: inputTarget?.value || null,
      inputType: inputTarget?.type || null,
      targetProductUrl: inputTarget?.dataset?.productUrl || null,
      isCombinedProduct,
      targetUrl: targetUrl || null,
      selectionsBeforeChange
    });

    if (isCombinedProduct) {
      this.sveaLog('info', 'variant.change.combined-product-fetch', { targetUrl });
      this.fetchProductSection(targetUrl, { source: 'variant-option-change' });
      return;  // Stop execution if false
    }

    this.applyVariantSelectionState('user-change');
  }

  combinedListingCheck(e) {
    const currentUrl = window.location.pathname;
    const inputTarget = e?.target || null;
    const targetUrl = this.getTargetProductUrlFromInput(inputTarget);
    const currentPath = this.getPathname(currentUrl);
    const targetPath = this.getPathname(targetUrl);
    let isCombinedProduct = false;

    if (targetPath && currentPath && targetPath !== currentPath) {
      isCombinedProduct = true;
    }

    return { isCombinedProduct, targetUrl };
  }

  getPathname(rawUrl) {
    if (!rawUrl) return '';
    try {
      const parsed = new URL(String(rawUrl), window.location.origin);
      return parsed.pathname;
    } catch (e) {
      return '';
    }
  }

  getTargetProductUrlFromInput(inputTarget) {
    if (!inputTarget) return null;

    const direct = String(inputTarget?.dataset?.productUrl || '').trim();
    if (direct.length > 0) return direct;

    if (inputTarget instanceof HTMLSelectElement) {
      const selectedOption = inputTarget.options?.[inputTarget.selectedIndex] || null;
      const fromOption = String(selectedOption?.dataset?.productUrl || '').trim();
      if (fromOption.length > 0) return fromOption;
    }

    return null;
  }

  bindSelectionPersistenceOnLinks() {
    const productElement = this.closest('.main-product');
    if (!productElement) return;
    if (productElement.dataset.pdpSelectionPersistenceBound === 'true') return;
    productElement.dataset.pdpSelectionPersistenceBound = 'true';

    productElement.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const link = target.closest('a[data-pdp-ajax-link][href]');
      if (!link || !productElement.contains(link)) return;
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      this.storePendingSelectionForPath(link.getAttribute('href'), 'sibling-link-click');
    });
  }

  normalizeOptionName(rawName) {
    const name = String(rawName || '').trim();
    if (!name) return '';
    const match = name.match(/^options\[(.*)\]$/i);
    return String(match ? match[1] : name).trim().toLowerCase();
  }

  normalizeOptionValue(rawValue) {
    return String(rawValue || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  getVariantLogStorageKey() {
    return '__variant_selection_logs_v1';
  }

  isVariantSelectionVerboseLoggingEnabled() {
    return window.__variantSelectionVerboseLogs === true;
  }

  variantLog(event, details = {}) {
    if (!this.isVariantSelectionVerboseLoggingEnabled()) return;

    const entry = {
      timestamp: new Date().toISOString(),
      event: String(event || 'unknown'),
      sectionId: this.dataset.section || null,
      productId: this.dataset.productId || null,
      page: `${window.location.pathname}${window.location.search}`,
      details
    };

    try {
      const storageKey = this.getVariantLogStorageKey();
      const raw = window.sessionStorage.getItem(storageKey) || '[]';
      const parsed = JSON.parse(raw);
      const existing = Array.isArray(parsed) ? parsed : [];
      existing.push(entry);
      const bounded = existing.slice(-500);
      window.sessionStorage.setItem(storageKey, JSON.stringify(bounded));
      window.__variantSelectionLogs = bounded;
      window.getVariantSelectionLogs = window.getVariantSelectionLogs || (() => {
        try {
          const stored = window.sessionStorage.getItem(storageKey) || '[]';
          const data = JSON.parse(stored);
          return Array.isArray(data) ? data : [];
        } catch (e) {
          return Array.isArray(window.__variantSelectionLogs) ? window.__variantSelectionLogs.slice() : [];
        }
      });
      window.clearVariantSelectionLogs = window.clearVariantSelectionLogs || (() => {
        try {
          window.sessionStorage.removeItem(storageKey);
        } catch (e) {}
        window.__variantSelectionLogs = [];
        return [];
      });
    } catch (e) {}

    console.log(`[variant-selection] ${entry.event}`, entry);
  }

  getPendingCombinedSelectionStorageKey() {
    return '__pdp_pending_combined_selection_v1';
  }

  setPendingCombinedSelectionState(state) {
    const payload = state && typeof state === 'object' ? state : null;
    if (!payload) return;
    window.__pdpPendingCombinedSelection = payload;
    try {
      window.sessionStorage.setItem(this.getPendingCombinedSelectionStorageKey(), JSON.stringify(payload));
    } catch (e) {}
  }

  readPendingCombinedSelectionState() {
    const fromMemory = window.__pdpPendingCombinedSelection;
    if (fromMemory && typeof fromMemory === 'object') return fromMemory;
    try {
      const raw = window.sessionStorage.getItem(this.getPendingCombinedSelectionStorageKey());
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  clearPendingCombinedSelectionState() {
    window.__pdpPendingCombinedSelection = null;
    try {
      window.sessionStorage.removeItem(this.getPendingCombinedSelectionStorageKey());
    } catch (e) {}
  }

  storePendingSelectionForPath(targetUrl, source = 'unknown') {
    const targetPath = this.getPathname(targetUrl);
    const currentPath = this.getPathname(window.location.pathname);
    if (!targetPath || targetPath === currentPath) {
      return { stored: false, reason: 'same-or-missing-target-path', targetPath, currentPath };
    }

    const selectedOptions = this.captureSelectedOptions();
    this.setPendingCombinedSelectionState({
      createdAt: Date.now(),
      sectionId: this.dataset.section || null,
      source,
      currentPath,
      targetPath,
      targetUrl,
      selectedOptions
    });

    const details = {
      source,
      currentPath,
      targetPath,
      selectedOptions
    };
    this.variantLog('combined.selection.stored', details);
    return { stored: true, ...details };
  }

  captureSelectedOptionsFromPicker(pickerRoot = null) {
    const root = pickerRoot || this;
    if (!root || !root.querySelectorAll) return [];

    const optionWrappers = Array.from(root.querySelectorAll('.product-form__input'));
    return optionWrappers
      .map((wrapper, index) => {
        const checkedRadio = wrapper.querySelector('input[type="radio"]:checked');
        if (checkedRadio) {
          return {
            index,
            name: this.normalizeOptionName(checkedRadio.name),
            value: checkedRadio.value,
            optionValueId: checkedRadio.dataset.optionValueId || null,
            productUrl: checkedRadio.dataset.productUrl || null
          };
        }

        const select = wrapper.querySelector('select');
        if (select) {
          const selectedOption = select.options?.[select.selectedIndex] || select.querySelector('option:checked');
          return {
            index,
            name: this.normalizeOptionName(select.name),
            value: select.value,
            optionValueId: selectedOption?.dataset?.optionValueId || null,
            productUrl: selectedOption?.dataset?.productUrl || null
          };
        }

        return null;
      })
      .filter((entry) => entry && String(entry.value || '').length > 0);
  }

  captureSelectedOptions() {
    return this.captureSelectedOptionsFromPicker(this);
  }

  canSelectRadioInput(input) {
    if (!input) return false;
    if (input.disabled) return false;
    return true;
  }

  canSelectOptionElement(option) {
    if (!option) return false;
    if (option.disabled) return false;
    return true;
  }

  restorePendingCombinedSelectionState() {
    const pending = this.readPendingCombinedSelectionState();
    if (!pending) return { appliedCount: 0, reason: 'no-pending-state' };

    const maxAgeMs = 30000;
    const createdAt = Number(pending.createdAt || 0);
    const ageMs = createdAt ? (Date.now() - createdAt) : Number.POSITIVE_INFINITY;
    if (!Number.isFinite(ageMs) || ageMs > maxAgeMs) {
      this.clearPendingCombinedSelectionState();
      return { appliedCount: 0, reason: 'pending-state-expired', ageMs };
    }

    if (String(pending.sectionId || '') !== String(this.dataset.section || '')) {
      return { appliedCount: 0, reason: 'pending-state-section-mismatch' };
    }

    const currentPath = this.getPathname(window.location.pathname);
    const pendingTargetPath = this.getPathname(pending.targetPath || pending.targetUrl);
    if (pendingTargetPath && currentPath && pendingTargetPath !== currentPath) {
      this.clearPendingCombinedSelectionState();
      return {
        appliedCount: 0,
        reason: 'pending-state-target-mismatch',
        currentPath,
        pendingTargetPath
      };
    }

    const filteredSelections = Array.isArray(pending.selectedOptions)
      ? pending.selectedOptions.filter((entry) => {
        const entryPath = this.getPathname(entry?.productUrl);
        if (!entryPath) return true;
        return entryPath === currentPath;
      })
      : [];

    const productElement = this.closest('.main-product');
    const result = this.reapplySelectionsToCombinedProduct(productElement, filteredSelections, {
      dispatchChange: false,
      source: 'pending-restore'
    });
    this.clearPendingCombinedSelectionState();
    return result;
  }

  reapplySelectionsToCombinedProduct(productElement, selectedOptions, options = {}) {
    const { dispatchChange = true, source = 'reapply' } = options || {};
    if (!productElement || !Array.isArray(selectedOptions) || selectedOptions.length === 0) {
      return { pickerFound: false, appliedCount: 0, changed: false, source, reason: 'missing-input' };
    }

    const picker = productElement.querySelector('variant-radios:not([hidden]), variant-selects:not([hidden]), variant-radios, variant-selects');
    if (!picker) {
      return { pickerFound: false, appliedCount: 0, changed: false, source, reason: 'picker-not-found' };
    }

    const optionWrappers = Array.from(picker.querySelectorAll('.product-form__input')).map((wrapper, index) => {
      const radio = wrapper.querySelector('input[type="radio"]');
      const select = wrapper.querySelector('select');
      const rawName = radio?.name || select?.name || '';
      return {
        wrapper,
        index,
        name: this.normalizeOptionName(rawName)
      };
    });

    let lastUpdatedControl = null;
    let appliedCount = 0;

    selectedOptions.forEach((selection) => {
      const wrapperByName = selection.name
        ? optionWrappers.find((entry) => entry.name && entry.name === selection.name)
        : null;
      const targetWrapper = (wrapperByName || optionWrappers[selection.index])?.wrapper;
      if (!targetWrapper) return;

      const radios = Array.from(targetWrapper.querySelectorAll('input[type="radio"]'));
      if (radios.length > 0) {
        const wantedValue = this.normalizeOptionValue(selection.value);
        const matchingRadio = radios.find((radio) => {
          return this.normalizeOptionValue(radio.value) === wantedValue && this.canSelectRadioInput(radio);
        });
        if (!matchingRadio || matchingRadio.checked) return;
        matchingRadio.checked = true;
        lastUpdatedControl = matchingRadio;
        appliedCount += 1;
        return;
      }

      const select = targetWrapper.querySelector('select');
      if (!select) return;
      const wantedValue = this.normalizeOptionValue(selection.value);
      const matchingOption = Array.from(select.options).find((option) => {
        return this.normalizeOptionValue(option.value) === wantedValue && this.canSelectOptionElement(option);
      });
      if (!matchingOption || select.value === matchingOption.value) return;
      select.value = matchingOption.value;
      lastUpdatedControl = select;
      appliedCount += 1;
    });

    if (dispatchChange && lastUpdatedControl) {
      lastUpdatedControl.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const snapshot = this.captureSelectedOptionsFromPicker(picker);
    const result = {
      pickerFound: true,
      appliedCount,
      changed: appliedCount > 0,
      dispatchedChange: !!(dispatchChange && lastUpdatedControl),
      source,
      snapshot
    };

    this.variantLog('selection.reapply', result);
    return result;
  }

  applyVariantSelectionState(reason = 'programmatic') {
    this.updateOptions();
    this.updateMasterId();
    this.updatePickupAvailability();
    this.updateInventoryStatus();
    this.removeErrorMessage();
    const selectionAdjusted = this.updateVariantStatuses();

    if (selectionAdjusted) {
      const details = {
        reason,
        selectionAdjusted: true,
        currentVariantId: this.currentVariant?.id || null,
        selections: this.captureSelectedOptions()
      };
      this.variantLog('variant.selection.adjusted', details);
      return details;
    }

    if (!this.currentVariant) {
      this.sveaLog('warn', 'variant.change.no-current-variant');
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
      const details = {
        reason,
        selectionAdjusted: false,
        currentVariantId: null,
        selections: this.captureSelectedOptions()
      };
      this.variantLog('variant.selection.no-current-variant', details);
      return details;
    }

    this.sveaLog('info', 'variant.change.current-variant', {
      variantId: this.currentVariant.id,
      variantSku: this.currentVariant.sku || null,
      variantPrice: this.currentVariant.price || null,
      variantAvailable: this.currentVariant.available === true
    });
    this.updateMedia();
    this.updateDimensions();
    this.updateURL();
    this.updateVariantInput();
    this.dispatchVariantChange();
    this.updateSveaWidget();
    this.renderProductInfo();
    this.updateShareUrl();

    const details = {
      reason,
      selectionAdjusted: false,
      currentVariantId: this.currentVariant.id,
      currentVariantSku: this.currentVariant.sku || null,
      currentVariantAvailable: this.currentVariant.available === true,
      selections: this.captureSelectedOptions()
    };
    this.variantLog('variant.selection.applied', details);
    return details;
  }

  async fetchProductSection(newProductUrl, options = {}) {
    const source = String(options?.source || 'unknown');
    const productElement = this.closest('.main-product');
    const productSectionId = this.dataset.section;
    if (!productElement || !productSectionId) return;
    const targetPath = this.getPathname(newProductUrl);
    const currentPath = this.getPathname(window.location.pathname);
    if (!targetPath || targetPath === currentPath) return;

    const storeResult = this.storePendingSelectionForPath(newProductUrl, source);
    const destination = new URL(String(newProductUrl), window.location.origin);

    this.sveaLog('info', 'combined.fetch.start', {
      source,
      currentPath,
      targetPath,
      productSectionId
    });
    this.variantLog('combined.navigation.start', {
      source,
      currentPath,
      targetPath,
      storedSelectionState: storeResult?.stored === true
    });

    productElement.setAttribute('data-pdp-loading', '1');

    try {
      window.location.assign(`${destination.pathname}${destination.search}${destination.hash}`);
      this.sveaLog('info', 'combined.navigation.redirect', {
        source,
        targetPath
      });
      this.variantLog('combined.navigation.redirect', {
        source,
        targetPath
      });
    } catch (error) {
      this.sveaLog('error', 'combined.navigation.error', {
        source,
        targetPath,
        message: error?.message || String(error)
      });
      this.variantLog('combined.navigation.error', {
        source,
        targetPath,
        message: error?.message || String(error),
        storeResult
      });
      this.clearPendingCombinedSelectionState();
      productElement.removeAttribute('data-pdp-loading');
    } finally {
      if (document.visibilityState !== 'hidden') {
        productElement.removeAttribute('data-pdp-loading');
      }
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    const variants = this.getVariantData() || [];
    if (!variants.length) {
      this.currentVariant = null;
      return;
    }

    if (!this.options || !this.options.length) {
      const formId = `product-form-${this.dataset.section}`;
      const selectedId = document.querySelector(`#${formId} input[name="id"]`)?.value;
      this.currentVariant = variants.find((variant) => String(variant.id) === String(selectedId)) || variants[0];
      return;
    }

    this.currentVariant = variants.find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });

    if (!this.currentVariant && variants.length === 1) {
      this.currentVariant = variants[0];
    }
  }

  getDefaultOgMediaId() {
    const mediaId = String(this.dataset.ogMediaId || '').trim();
    return mediaId || null;
  }

  hasSiblingProductsForDefaultMedia() {
    return String(this.dataset.hasSiblingProducts || '').toLowerCase() === 'true';
  }

  isLegSetProductForDefaultMedia() {
    return String(this.dataset.isLegSetProduct || '').toLowerCase() === 'true';
  }

  shouldPreferOgMediaOnLoad() {
    if (this.userHasInteractedWithVariant) return false;
    if (!this.hasSiblingProductsForDefaultMedia()) return false;
    if (this.isLegSetProductForDefaultMedia()) return false;
    return true;
  }

  updateMedia() {
    if (!this.currentVariant) return;

    const mediaGridySlider = document.getElementById(`slider-${this.dataset.section}`);
    const thumbnailsGridySlider = document.getElementById(`thumbnail-slider-${this.dataset.section}`);
    const zoomGridySlider = document.getElementById(`zoom-slider-${this.dataset.section}`);
    const debugMedia = /(?:[?&])debugMedia=1(?:&|$)/.test(window.location.search);

    const visibility = this.updateMediaVisibility() || {};

    const featuredMediaId = this.currentVariant.featured_media?.id;
    const ogMediaId = this.getDefaultOgMediaId();
    const preferOgOnLoad = this.shouldPreferOgMediaOnLoad();
    const hasVisibleOg = !!(preferOgOnLoad && ogMediaId && visibility.visibleIds?.has(String(ogMediaId)));
    const hasVisibleFeatured = !!(featuredMediaId && visibility.visibleIds?.has(String(featuredMediaId)));
    let targetMediaId = null;
    if (hasVisibleOg) {
      targetMediaId = ogMediaId;
    } else if (hasVisibleFeatured) {
      targetMediaId = featuredMediaId;
    } else if (visibility.firstVisibleId) {
      targetMediaId = visibility.firstVisibleId;
    } else if (preferOgOnLoad && ogMediaId) {
      targetMediaId = ogMediaId;
    }

    if (debugMedia) {
      console.groupCollapsed('[media-debug] updateMedia', {
        variantId: this.currentVariant.id,
        sku: this.currentVariant.sku,
        ogMediaId,
        preferOgOnLoad,
        featuredMediaId,
        targetMediaId,
        firstVisibleId: visibility.firstVisibleId,
        visibleIds: Array.from(visibility.visibleIds || [])
      });
    }

    if (targetMediaId) {
      const targetItem = document.getElementById(`item-${targetMediaId}`);
      if (targetItem) {
        targetItem.hidden = false;
        targetItem.style.position = '';
        targetItem.style.top = '';
        targetItem.style.left = '';
        targetItem.style.width = '';
        targetItem.style.height = '';
        targetItem.style.margin = '';
        targetItem.style.pointerEvents = '';
        targetItem.style.transform = '';
        targetItem.style.transition = '';
        targetItem.style.opacity = '1';
        const img = targetItem.querySelector('img');
        if (img) {
          img.loading = 'eager';
          img.fetchPriority = 'high';
          if (!img.currentSrc && img.getAttribute('src')) {
            img.src = img.getAttribute('src');
          } else if (img.complete === false && img.getAttribute('src')) {
            img.src = img.getAttribute('src');
          }
          img.decode?.().catch(() => {});
        }
        if (debugMedia) {
          const figure = targetItem.querySelector('figure');
          const itemStyles = window.getComputedStyle(targetItem);
          const figStyles = figure ? window.getComputedStyle(figure) : null;
          const imgStyles = img ? window.getComputedStyle(img) : null;
          console.log('[media-debug] target item state', {
            id: targetItem.id,
            hidden: targetItem.hidden,
            display: itemStyles.display,
            visibility: itemStyles.visibility,
            opacity: itemStyles.opacity,
            width: itemStyles.width,
            height: itemStyles.height,
            classes: targetItem.className
          });
          if (figure) {
            console.log('[media-debug] target figure state', {
              display: figStyles.display,
              visibility: figStyles.visibility,
              opacity: figStyles.opacity,
              width: figStyles.width,
              height: figStyles.height,
              classes: figure.className
            });
          }
          if (img) {
            console.log('[media-debug] target img state', {
              currentSrc: img.currentSrc,
              src: img.getAttribute('src'),
              naturalWidth: img.naturalWidth,
              naturalHeight: img.naturalHeight,
              complete: img.complete,
              display: imgStyles.display,
              visibility: imgStyles.visibility,
              opacity: imgStyles.opacity,
              width: imgStyles.width,
              height: imgStyles.height
            });
          } else {
            console.warn('[media-debug] target img missing', { id: targetItem.id });
          }
        }
      }
    }

    if (mediaGridySlider) {
      const track = mediaGridySlider.querySelector('.gridy-track');
      if (debugMedia && track) {
        console.log('[media-debug] main track before', {
          scrollLeft: track.scrollLeft,
          scrollWidth: track.scrollWidth,
          clientWidth: track.clientWidth
        });
      }
      if (track) {
        const prevDisplay = track.style.display;
        track.style.display = 'none';
        track.offsetHeight;
        track.style.display = prevDisplay;
      }
      if (targetMediaId) {
        mediaGridySlider.refreshSlides?.();
        mediaGridySlider.scrollToSlideByID(`item-${targetMediaId}`);
        if (track) {
          const targetItem = document.getElementById(`item-${targetMediaId}`);
          if (targetItem) {
            const baseOffset = track.firstElementChild ? track.firstElementChild.offsetLeft : 0;
            const nextLeft = Math.max(0, targetItem.offsetLeft - baseOffset);
            track.scrollTo({ left: nextLeft, top: 0, behavior: "instant" });
            if (debugMedia) {
              console.log('[media-debug] main track scroll instant', {
                targetId: `item-${targetMediaId}`,
                nextLeft
              });
            }
          }
        }
        setTimeout(() => {
          mediaGridySlider.refreshSlides?.();
          mediaGridySlider.scrollToSlideByID(`item-${targetMediaId}`);
          if (track) {
            const targetItem = document.getElementById(`item-${targetMediaId}`);
            if (targetItem) {
              const baseOffset = track.firstElementChild ? track.firstElementChild.offsetLeft : 0;
              const nextLeft = Math.max(0, targetItem.offsetLeft - baseOffset);
              track.scrollTo({ left: nextLeft, top: 0, behavior: "instant" });
              if (debugMedia) {
                console.log('[media-debug] main track scroll instant (delayed)', {
                  targetId: `item-${targetMediaId}`,
                  nextLeft
                });
              }
            }
          }
        }, 260);
      }
      if (debugMedia && track) {
        const visibleItems = Array.from(track.querySelectorAll('[data-media-item]')).filter((item) => !item.hidden);
        console.log('[media-debug] main track visible items', visibleItems.map((item) => item.id));
      }
    }

    if (thumbnailsGridySlider) {
      if (targetMediaId) {
        thumbnailsGridySlider.scrollToSlideByID(`thumbnail-item-${targetMediaId}`);
      }
    }

    this.updateThumbnailActive(targetMediaId);

    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    const isGrid = mediaGridySlider && mediaGridySlider.dataset.desktopEnabled === 'false' ? true : false;
    if (isDesktop && isGrid && targetMediaId) {
      document.getElementById(`item-${targetMediaId}`)?.scrollIntoView();
    }

    if (debugMedia) {
      console.groupEnd();
    }
  }

  updateMediaVisibility() {
    if (!this.currentVariant) return;

    const variantId = String(this.currentVariant.id);
    const mediaGridySlider = document.getElementById(`slider-${this.dataset.section}`);
    const thumbnailsGridySlider = document.getElementById(`thumbnail-slider-${this.dataset.section}`);
    const zoomGridySlider = document.getElementById(`zoom-slider-${this.dataset.section}`);

    if (!mediaGridySlider) return;
    const mediaTrack = mediaGridySlider.querySelector('.gridy-track');
    if (!mediaTrack) return;

    const mediaItems = Array.from(mediaGridySlider.querySelectorAll('[data-media-item]'));
    const getIds = (item, key) =>
      (item.dataset[key] || '').split(',').map((id) => id.trim()).filter(Boolean);
    const getFilename = (item, key) => {
      const raw = (item.dataset[key] || '').toLowerCase();
      if (raw) return raw;

      const img = item.querySelector && item.querySelector('img');
      const src = (img?.currentSrc || img?.getAttribute?.('src') || '').toLowerCase();
      if (!src) return '';
      const base = src.split('?')[0];
      const parts = base.split('/');
      return parts[parts.length - 1] || '';
    };
    const allSkus = (this.variantData || this.getVariantData())
      .map((variant) => String(variant.sku || '').trim())
      .filter(Boolean);
    const skuSet = new Set(allSkus.map((sku) => sku.toLowerCase()));

    const currentSku = String(this.currentVariant.sku || '').trim().toLowerCase();
    const hasAnyVariantMedia = mediaItems.some((item) => getIds(item, 'mediaVariantIds').length > 0);
    const showAll = !hasAnyVariantMedia && skuSet.size === 0;
    const debugMedia = /(?:[?&])debugMedia=1(?:&|$)/.test(window.location.search);
    if (debugMedia) {
      console.groupCollapsed('[media-debug] variant', {
        id: this.currentVariant.id,
        sku: currentSku,
        options: this.currentVariant.options
      });
      console.log('variants available', (this.variantData || this.getVariantData()).map((v) => ({
        id: v.id,
        sku: v.sku,
        options: v.options
      })));
      console.log('media items', mediaItems.map((item) => ({
        id: item.id,
        filename: getFilename(item, 'mediaFilename'),
        variantIds: getIds(item, 'mediaVariantIds')
      })));
      console.groupEnd();
    }

    const skuList = Array.from(skuSet).sort((a, b) => b.length - a.length);
    const getMatchedSkus = (filename) => {
      if (!filename || skuList.length === 0) return [];
      const matches = [];
      for (const sku of skuList) {
        if (!sku) continue;
        const re = new RegExp(`(^|[^a-z0-9])${sku}([^a-z0-9]|$)`, 'i');
        if (re.test(filename)) matches.push(sku);
      }
      return matches;
    };

    const shouldShowMedia = (datasetKey, filenameKey) => (item) => {
      const filename = getFilename(item, filenameKey);
      const matchedSkus = getMatchedSkus(filename);
      const ids = getIds(item, datasetKey);

      if (matchedSkus.length) {
        const hasCurrentSku = currentSku && matchedSkus.includes(currentSku);
        if (debugMedia) {
          console.log('[media-debug] sku-match(es)', {
            mediaId: item.id,
            filename,
            matchedSkus,
            currentSku
          });
        }
        return hasCurrentSku;
      }
      if (ids.length) {
        if (debugMedia) {
          console.log('[media-debug] variant-ids match', {
            mediaId: item.id,
            filename,
            ids,
            variantId
          });
        }
        return ids.includes(variantId);
      }
      if (showAll) return true;
      if (!filename) return false;
      return true;
    };

    const nextVisibility = new Map(
      mediaItems.map((item) => [item, shouldShowMedia('mediaVariantIds', 'mediaFilename')(item)])
    );
    const ogMediaId = this.getDefaultOgMediaId();
    const shouldPreferOgOnLoad = this.shouldPreferOgMediaOnLoad() && !!ogMediaId;
    const featuredId = this.currentVariant.featured_media?.id ? `item-${this.currentVariant.featured_media.id}` : null;
    if (featuredId && !nextVisibility.get(document.getElementById(featuredId))) {
      const featuredItem = mediaItems.find((item) => item.id === featuredId);
      if (featuredItem) nextVisibility.set(featuredItem, true);
    }
    if (shouldPreferOgOnLoad) {
      const ogItem = mediaItems.find((item) => item.id === `item-${ogMediaId}`);
      if (ogItem) nextVisibility.set(ogItem, true);
    }

    let visibleItemsNext = mediaItems.filter((item) => nextVisibility.get(item));
    if (visibleItemsNext.length === 0) {
      let fallbackItems = [];
      if (currentSku) {
        fallbackItems = mediaItems.filter((item) => {
          const filename = getFilename(item, 'mediaFilename');
          return filename && filename.includes(currentSku);
        });
      }
      if (fallbackItems.length === 0 && variantId) {
        fallbackItems = mediaItems.filter((item) => getIds(item, 'mediaVariantIds').includes(variantId));
      }
      if (fallbackItems.length === 0 && mediaItems.length) {
        fallbackItems = [mediaItems[0]];
      }
      fallbackItems.forEach((item) => nextVisibility.set(item, true));
      visibleItemsNext = mediaItems.filter((item) => nextVisibility.get(item));
      if (debugMedia) {
        console.warn('[media-debug] fallback used', {
          currentSku,
          variantId,
          fallbackIds: fallbackItems.map((item) => item.id)
        });
      }
    }
    const visibleIds = new Set(
      visibleItemsNext.map((item) => item.id?.replace('item-', '')).filter(Boolean)
    );
    const firstVisibleId = visibleItemsNext[0]?.id?.replace('item-', '');

    const firstRects = new Map();
    mediaItems.forEach((item) => {
      if (!item.hidden && nextVisibility.get(item)) {
        firstRects.set(item, item.getBoundingClientRect());
      }
    });

    const isSliderLayout = mediaGridySlider && (mediaGridySlider.dataset.desktopEnabled === 'true' || mediaGridySlider.dataset.mobileEnabled === 'true');
    const fadeDuration = 220;
    if (!isSliderLayout) {
      mediaItems.forEach((item) => {
        const willShow = nextVisibility.get(item);
        if (willShow) {
          item.hidden = false;
          item.style.position = '';
          item.style.top = '';
          item.style.left = '';
          item.style.width = '';
          item.style.height = '';
          item.style.margin = '';
          item.style.pointerEvents = '';
          item.style.transform = '';
          item.style.transition = '';
          item.style.opacity = '1';
        }
      });

      mediaItems.forEach((item) => {
        const willShow = nextVisibility.get(item);
        if (!willShow && !item.hidden) {
          const rect = item.getBoundingClientRect();
          const trackRect = mediaTrack.getBoundingClientRect();
          item.style.position = 'absolute';
          item.style.top = `${rect.top - trackRect.top + mediaTrack.scrollTop}px`;
          item.style.left = `${rect.left - trackRect.left + mediaTrack.scrollLeft}px`;
          item.style.width = `${rect.width}px`;
          item.style.height = `${rect.height}px`;
          item.style.margin = '0';
          item.style.pointerEvents = 'none';
          item.style.opacity = '1';

          item.animate([{ opacity: 1 }, { opacity: 0 }], {
            duration: fadeDuration,
            easing: 'ease',
            fill: 'forwards'
          }).onfinish = () => {
            item.hidden = true;
            item.style.position = '';
            item.style.top = '';
            item.style.left = '';
            item.style.width = '';
            item.style.height = '';
            item.style.margin = '';
            item.style.pointerEvents = '';
            item.style.opacity = '';
          };
        }
      });

      requestAnimationFrame(() => {
        mediaItems.forEach((item) => {
          const willShow = nextVisibility.get(item);
          if (willShow && item.style.opacity === '0') {
            item.style.transition = `opacity ${fadeDuration}ms ease`;
            item.style.opacity = '1';
          }
        });

        mediaItems.forEach((item) => {
          if (!nextVisibility.get(item)) return;
          const first = firstRects.get(item);
          if (!first) return;
          const last = item.getBoundingClientRect();
          const dx = first.left - last.left;
          const dy = first.top - last.top;
          if (dx || dy) {
            item.style.transform = `translate(${dx}px, ${dy}px)`;
            item.style.transition = `transform ${fadeDuration}ms ease`;
            requestAnimationFrame(() => {
              item.style.transform = '';
            });
            item.addEventListener('transitionend', () => {
              item.style.transition = '';
            }, { once: true });
          }
        });
      });
    } else {
      mediaItems.forEach((item) => {
        const willShow = nextVisibility.get(item);
        item.hidden = !willShow;
        item.style.position = '';
        item.style.top = '';
        item.style.left = '';
        item.style.width = '';
        item.style.height = '';
        item.style.margin = '';
        item.style.pointerEvents = '';
        item.style.transform = '';
        item.style.transition = '';
        item.style.opacity = willShow ? '1' : '';
      });
    }

    if (thumbnailsGridySlider) {
      const thumbItems = Array.from(thumbnailsGridySlider.querySelectorAll('[data-thumbnail-btn]'));
      thumbItems.forEach((item) => {
        item.hidden = !shouldShowMedia('thumbnailVariantIds', 'thumbnailFilename')(item);
      });
      if (shouldPreferOgOnLoad) {
        const ogThumb = document.getElementById(`thumbnail-item-${ogMediaId}`);
        if (ogThumb) ogThumb.hidden = false;
      }
      thumbnailsGridySlider.refreshSlides?.();
    }

    if (zoomGridySlider) {
      if (debugMedia) {
        const zoomBefore = Array.from(zoomGridySlider.querySelectorAll('[data-zoom-item]')).filter((item) => !item.hidden);
        console.log('[media-debug] zoom visible items (before)', zoomBefore.map((item) => item.id));
      }
      const zoomItems = Array.from(zoomGridySlider.querySelectorAll('[data-zoom-item]'));
      zoomItems.forEach((item) => {
        item.hidden = !shouldShowMedia('zoomVariantIds', 'zoomFilename')(item);
      });
      if (shouldPreferOgOnLoad) {
        const ogZoomItem = document.getElementById(`modal-item-${ogMediaId}`);
        if (ogZoomItem) ogZoomItem.hidden = false;
      }
      zoomGridySlider.refreshSlides?.();

      const visibleZoomItems = zoomItems.filter((item) => !item.hidden);
      if (debugMedia) {
        console.log('[media-debug] zoom visible items (after)', visibleZoomItems.map((item) => item.id));
      }
      visibleZoomItems.forEach((item, index) => {
        const label = item.querySelector('.item-number span');
        if (label) label.textContent = `${index + 1} / ${visibleZoomItems.length}`;
      });
    }

    mediaGridySlider.refreshSlides?.();

    return {
      visibleIds,
      firstVisibleId
    };
  }

  updateThumbnailActive(targetMediaId) {
    const thumbnailsGridySlider = document.getElementById(`thumbnail-slider-${this.dataset.section}`);
    if (!thumbnailsGridySlider) return;

    const thumbItems = Array.from(thumbnailsGridySlider.querySelectorAll('[data-thumbnail-btn]'));
    thumbItems.forEach((item) => item.classList.remove('active'));

    let activeThumb = null;
    if (targetMediaId) {
      activeThumb = document.getElementById(`thumbnail-item-${targetMediaId}`);
      if (activeThumb && activeThumb.hidden) activeThumb = null;
    }

    if (!activeThumb) {
      activeThumb = thumbItems.find((item) => !item.hidden) || null;
    }

    if (activeThumb) activeThumb.classList.add('active');
  }

  updateDimensions() {
    if (!this.currentVariant) return;
    const scope = this.closest('section') || document;
    const details = scope.querySelector('[data-mitat]');
    if (!details) return;

    const content = details.querySelector('[data-mitat-content]');
    const dataScript = details.querySelector('[data-mitat-variants]');
    if (!content || !dataScript) return;

    let data = {};
    try {
      data = JSON.parse(dataScript.textContent);
    } catch (e) {
      return;
    }

    const entry = data[this.currentVariant.id];
    if (!entry || !entry.html) {
      content.innerHTML = '';
      details.hidden = true;
      return;
    }

    content.innerHTML = entry.html;
    details.hidden = false;
  }

  getProductBlockScope() {
    return (
      this.closest('.pdp-content')
      || this.closest('.main-product')
      || this.closest('.shopify-section')
      || this.closest('section')
      || document
    );
  }

  isEmptyTextProductBlock(blockNode) {
    const rte = blockNode?.querySelector?.('.rte');
    if (!rte) return false;

    const text = String(rte.textContent || '').replace(/\u00A0/g, ' ').trim();
    if (text.length > 0) return false;

    const leftoverHtml = String(rte.innerHTML || '')
      .replace(/<!--[\s\S]*?-->/g, '')
      .replace(/<(\/)?(p|br|span|strong|em|b|i|u|small|sub|sup|a|div)\b[^>]*>/gi, '')
      .replace(/&nbsp;/gi, '')
      .trim();

    return leftoverHtml.length === 0;
  }

  isEmptyCustomLiquidProductBlock(blockNode) {
    if (!blockNode) return false;

    const text = String(blockNode.textContent || '').replace(/\u00A0/g, ' ').trim();
    if (text.length > 0) return false;

    if (blockNode.querySelector('img,svg,video,iframe,canvas,hr,object,embed,table,ul,ol,dl,blockquote,pre')) {
      return false;
    }

    const meaningfulChildren = Array.from(blockNode.children).filter((node) => {
      const tag = String(node.tagName || '').toUpperCase();
      return tag !== 'SCRIPT' && tag !== 'STYLE' && tag !== 'TEMPLATE';
    });

    return meaningfulChildren.length === 0;
  }

  pruneEmptyProductBlocks(scope = null) {
    const root = scope || this.getProductBlockScope();
    if (!root || !root.querySelectorAll) return;

    const emptyTextBlocks = Array.from(root.querySelectorAll('.product-block.product-block__text'))
      .filter((node) => this.isEmptyTextProductBlock(node));
    const emptyCustomLiquidBlocks = Array.from(root.querySelectorAll('.product-block.product-block__custom_liquid'))
      .filter((node) => this.isEmptyCustomLiquidProductBlock(node));

    emptyTextBlocks.forEach((node) => node.remove());
    emptyCustomLiquidBlocks.forEach((node) => node.remove());
  }

  getSniffieOmnibusBlocks(scope = null) {
    const root = scope || this.getProductBlockScope();
    if (!root || !root.querySelectorAll) return [];

    return Array.from(root.querySelectorAll('.product-block')).filter((node) => {
      const className = typeof node.className === 'string' ? node.className : '';
      if (className.indexOf('product-block__@app') === -1) return false;
      const html = String(node.innerHTML || '').toLowerCase();
      return html.includes('sniffie') || html.includes('omnibus-price');
    });
  }

  hasSniffieOmnibusContent(blockNode) {
    if (!blockNode) return false;

    const roots = Array.from(blockNode.querySelectorAll('#sniffie-omnibus-price, [id*="sniffie-omnibus-price"], [id*="omnibus-price"]'));
    if (!roots.length) {
      const fallbackText = String(blockNode.textContent || '').replace(/\u00A0/g, ' ').trim();
      return fallbackText.length > 0;
    }

    return roots.some((root) => {
      const text = String(root.textContent || '').replace(/\u00A0/g, ' ').trim();
      if (text.length > 0) return true;

      return Array.from(root.querySelectorAll('*')).some((node) => {
        const tag = String(node.tagName || '').toUpperCase();
        if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'TEMPLATE') return false;
        if (node.matches && node.matches('img,svg,video,canvas,table,ul,ol,dl,blockquote,hr')) return true;
        const nodeText = String(node.textContent || '').replace(/\u00A0/g, ' ').trim();
        return nodeText.length > 0;
      });
    });
  }

  hasActivePricingSignal(scope = null) {
    const root = scope || this.getProductBlockScope();
    if (!root || !root.querySelector) return false;

    return !!(
      root.querySelector('[data-campaign-active="1"]')
      || root.querySelector('[data-campaign-validity]')
      || root.querySelector('[data-sale-validity-date]')
    );
  }

  syncSniffieOmnibusVisibility(blockNode) {
    if (!blockNode) return;
    const hasContent = this.hasSniffieOmnibusContent(blockNode);
    const hasPricingSignal = this.hasActivePricingSignal();
    blockNode.hidden = !(hasContent && hasPricingSignal);
  }

  bindSniffieOmnibusGuards(scope = null) {
    const root = scope || this.getProductBlockScope();
    const blocks = this.getSniffieOmnibusBlocks(root);
    if (!blocks.length) return;

    blocks.forEach((blockNode) => {
      this.syncSniffieOmnibusVisibility(blockNode);

      if (blockNode.dataset.sniffieOmnibusGuardBound === 'true') return;
      blockNode.dataset.sniffieOmnibusGuardBound = 'true';

      const observer = new MutationObserver(() => {
        this.syncSniffieOmnibusVisibility(blockNode);
      });
      observer.observe(blockNode, { childList: true, subtree: true, characterData: true });

      window.setTimeout(() => this.syncSniffieOmnibusVisibility(blockNode), 300);
      window.setTimeout(() => this.syncSniffieOmnibusVisibility(blockNode), 1000);
      window.setTimeout(() => this.syncSniffieOmnibusVisibility(blockNode), 1800);
    });
  }

  getSveaWidgetScope() {
    const sectionId = this.dataset.section;
    const shopifySection = sectionId ? document.getElementById(`shopify-section-${sectionId}`) : null;
    return (
      shopifySection
      || this.closest('.main-product')
      || this.closest('.shopify-section')
      || this.closest('section')
      || document
    );
  }

  getSveaWidgetRoots(scope) {
    const rootScope = scope || this.getSveaWidgetScope();
    const roots = new Set();

    rootScope.querySelectorAll('[data-svea-widget]').forEach((node) => roots.add(node));
    rootScope.querySelectorAll('script.svea-pp-widget-part-payment').forEach((scriptNode) => {
      const root = scriptNode.closest('[data-svea-widget]');
      if (root) roots.add(root);
    });

    return Array.from(roots);
  }

  getSveaToggleNode(widgetRoot) {
    if (!widgetRoot) return null;
    return widgetRoot.querySelector('[data-svea-toggle]');
  }

  getSveaPanelNode(widgetRoot) {
    if (!widgetRoot) return null;
    return widgetRoot.querySelector('[data-svea-panel]');
  }

  getSveaRuntimeNode(widgetRoot) {
    if (!widgetRoot) return null;
    return widgetRoot.querySelector('[data-svea-runtime]');
  }

  getSveaProviderDefaultSrc() {
    return 'https://payments.maksuturva.fi/tools/partpayment/partPayment.js';
  }

  isSveaProviderReady() {
    return !!(window.SveaPartPayment && typeof window.SveaPartPayment.initializeWidgets === 'function');
  }

  ensureSveaProviderLoaded(src) {
    const defaultSrc = this.getSveaProviderDefaultSrc();
    const safeSrc = String(src || defaultSrc).trim() || defaultSrc;

    if (this.isSveaProviderReady()) {
      this.sveaLog('info', 'provider.load.skip-ready', { src: safeSrc });
      return Promise.resolve(window.SveaPartPayment);
    }

    if (window.__sveaWidgetProviderLoadPromise && window.__sveaWidgetProviderLoadSrc === safeSrc) {
      this.sveaLog('info', 'provider.load.reuse-promise', { src: safeSrc });
      return window.__sveaWidgetProviderLoadPromise;
    }

    this.sveaLog('info', 'provider.load.start', { src: safeSrc });

    const loadPromise = new Promise((resolve, reject) => {
      const resolveIfReady = (stage) => {
        if (!this.isSveaProviderReady()) return false;
        this.sveaLog('info', 'provider.load.success', { src: safeSrc, stage });
        resolve(window.SveaPartPayment);
        return true;
      };

      if (resolveIfReady('already-ready')) return;

      let scriptNode = document.querySelector(`script[data-svea-provider="true"][src="${safeSrc}"]`);
      if (!scriptNode) {
        scriptNode = Array.from(document.scripts || []).find((node) => node.src === safeSrc) || null;
      }

      const bindListeners = (node, created) => {
        const cleanup = () => {
          node.removeEventListener('load', onLoad);
          node.removeEventListener('error', onError);
        };
        const onLoad = () => {
          cleanup();
          node.setAttribute('data-svea-provider-loaded', 'true');
          if (resolveIfReady(created ? 'created-script-load' : 'existing-script-load')) return;
          window.setTimeout(() => {
            if (resolveIfReady(created ? 'created-script-load-delayed' : 'existing-script-load-delayed')) return;
            const message = 'Svea provider loaded but API was not initialized';
            this.sveaLog('error', 'provider.load.api-missing', { src: safeSrc, created, message });
            reject(new Error(message));
          }, 120);
        };
        const onError = () => {
          cleanup();
          this.sveaLog('error', 'provider.load.error', { src: safeSrc, created });
          reject(new Error(`Failed to load Svea provider script: ${safeSrc}`));
        };

        node.setAttribute('data-svea-provider', 'true');
        node.addEventListener('load', onLoad);
        node.addEventListener('error', onError);

        if (node.readyState === 'complete') {
          window.setTimeout(onLoad, 0);
        }
      };

      if (scriptNode) {
        bindListeners(scriptNode, false);
        return;
      }

      scriptNode = document.createElement('script');
      scriptNode.src = safeSrc;
      scriptNode.async = true;
      bindListeners(scriptNode, true);
      (document.head || document.body || document.documentElement).appendChild(scriptNode);
    });

    window.__sveaWidgetProviderLoadPromise = loadPromise;
    window.__sveaWidgetProviderLoadSrc = safeSrc;

    return loadPromise.catch((error) => {
      if (window.__sveaWidgetProviderLoadPromise === loadPromise) {
        window.__sveaWidgetProviderLoadPromise = null;
        window.__sveaWidgetProviderLoadSrc = null;
      }
      throw error;
    });
  }

  refreshSveaProvider(priceText, widgetRoot = null, mode = 'update') {
    const providerReady = this.isSveaProviderReady();
    this.sveaLog('info', 'provider.refresh.begin', {
      providerReady,
      priceText,
      mode
    }, widgetRoot);

    if (!providerReady) return false;

    if (mode === 'initialize') {
      try {
        window.SveaPartPayment.initializeWidgets();
      } catch (error) {
        this.sveaLog('error', 'provider.refresh.init-error', {
          message: error?.message || String(error)
        }, widgetRoot);
      }
    }

    try {
      document.dispatchEvent(new CustomEvent('svea-partpayment-calculator-update-price', {
        detail: { price: priceText }
      }));
    } catch (error) {
      this.sveaLog('warn', 'provider.refresh.dispatch-error', {
        message: error?.message || String(error)
      }, widgetRoot);
    }

    this.sveaLog('info', 'provider.refresh.done', { priceText, mode }, widgetRoot);

    return true;
  }

  getSveaLogStorageKey() {
    return '__svea_widget_logs_v1';
  }

  initSveaLogger() {
    if (window.__sveaWidgetLoggerInitialized === true) return;

    const storageKey = this.getSveaLogStorageKey();
    let initialLogs = [];
    try {
      const raw = window.sessionStorage.getItem(storageKey) || '[]';
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) initialLogs = parsed;
    } catch (e) {
      initialLogs = [];
    }

    window.__sveaWidgetLogs = initialLogs;
    window.getSveaWidgetLogs = window.getSveaWidgetLogs || (() => {
      try {
        const raw = window.sessionStorage.getItem(storageKey) || '[]';
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return Array.isArray(window.__sveaWidgetLogs) ? window.__sveaWidgetLogs.slice() : [];
      }
    });
    window.clearSveaWidgetLogs = window.clearSveaWidgetLogs || (() => {
      try {
        window.sessionStorage.removeItem(storageKey);
      } catch (e) {}
      window.__sveaWidgetLogs = [];
      return [];
    });
    window.__sveaWidgetLoggerInitialized = true;
  }

  getSveaLogContext(widgetRoot = null) {
    return {
      sectionId: this.dataset.section || null,
      productId: this.dataset.productId || null,
      variantId: this.currentVariant?.id || null,
      variantSku: this.currentVariant?.sku || null,
      widgetKey: widgetRoot?.dataset?.sveaWidgetKey
        || widgetRoot?.id
        || widgetRoot?.querySelector?.('[data-svea-panel]')?.id
        || null,
      productHandle: widgetRoot?.dataset?.sveaProductHandle || null
    };
  }

  isSveaVerboseLoggingEnabled() {
    return window.__sveaWidgetVerboseLogs === true;
  }

  shouldEmitSveaLog(level, event) {
    return this.isSveaVerboseLoggingEnabled();
  }

  getSveaWidgetStatus(scope = null) {
    const rootScope = scope || this.getSveaWidgetScope();
    const widgets = this.getSveaWidgetRoots(rootScope).filter((node) => node.hasAttribute('data-svea-widget'));

    const details = widgets.map((widgetRoot) => {
      const panel = this.getSveaPanelNode(widgetRoot);
      const runtime = this.getSveaRuntimeNode(widgetRoot);
      const panelStyles = panel ? window.getComputedStyle(panel) : null;
      const hasScript = !!runtime?.querySelector('script.svea-pp-widget-part-payment');
      const hasRenderedWidget = !!runtime?.querySelector('[class*="svea-pp-widget"]');
      const panelVisible = !!(panel
        && panel.hidden !== true
        && panelStyles
        && panelStyles.display !== 'none'
        && panelStyles.visibility !== 'hidden'
        && panelStyles.opacity !== '0');
      return {
        widgetKey: widgetRoot?.dataset?.sveaWidgetKey || null,
        hasScript,
        hasRenderedWidget,
        panelVisible,
        unavailable: widgetRoot.classList.contains('is-unavailable')
      };
    });

    const renderedCount = details.filter((item) => item.hasRenderedWidget).length;
    const visibleCount = details.filter((item) => item.panelVisible).length;
    const provider = window.SveaPartPayment || null;
    return {
      page: `${window.location.pathname}${window.location.search}`,
      widgetCount: widgets.length,
      renderedCount,
      visibleCount,
      isWidgetVisible: renderedCount > 0 && visibleCount > 0,
      providerReady: !!(provider && typeof provider.initializeWidgets === 'function'),
      providerInitialLoadDone: provider?.initialLoadDone === true,
      widgets: details
    };
  }

  logSveaStatus(stage, extra = {}, scope = null) {
    const status = this.getSveaWidgetStatus(scope);
    this.sveaLog('info', 'status', { stage, ...status, ...extra });
  }

  sveaLog(level, event, details = {}, widgetRoot = null) {
    const validLevels = ['debug', 'info', 'warn', 'error'];
    const safeLevel = validLevels.includes(level) ? level : 'info';
    if (!this.shouldEmitSveaLog(safeLevel, event)) return;
    this.initSveaLogger();

    const entry = {
      timestamp: new Date().toISOString(),
      level: safeLevel,
      event: String(event || 'unknown'),
      url: `${window.location.pathname}${window.location.search}`,
      ...this.getSveaLogContext(widgetRoot),
      details
    };

    try {
      const storageKey = this.getSveaLogStorageKey();
      const existing = Array.isArray(window.__sveaWidgetLogs) ? window.__sveaWidgetLogs.slice() : [];
      existing.push(entry);
      const bounded = existing.slice(-400);
      window.__sveaWidgetLogs = bounded;
      window.sessionStorage.setItem(storageKey, JSON.stringify(bounded));
    } catch (e) {}

    const line = `[svea-widget][${safeLevel}] ${entry.event}`;
    if (safeLevel === 'error') {
      console.error(line, entry);
    } else if (safeLevel === 'warn') {
      console.warn(line, entry);
    } else {
      console.log(line, entry);
    }
  }

  isSveaAlwaysOpen(widgetRoot) {
    return widgetRoot?.dataset?.sveaAlwaysOpen === 'true';
  }

  setSveaWidgetOpen(widgetRoot, shouldOpen, immediate = false) {
    if (!widgetRoot) return;

    const panel = this.getSveaPanelNode(widgetRoot);
    const toggle = this.getSveaToggleNode(widgetRoot);
    const runtime = this.getSveaRuntimeNode(widgetRoot);
    if (!panel || !runtime) return;

    if (widgetRoot.classList.contains('is-unavailable')) {
      shouldOpen = false;
    }
    if (this.isSveaAlwaysOpen(widgetRoot) && !widgetRoot.classList.contains('is-unavailable')) {
      shouldOpen = true;
    }

    widgetRoot.classList.toggle('is-open', shouldOpen);
    if (toggle) {
      toggle.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    }
    panel.hidden = false;

    if (shouldOpen) {
      panel.style.opacity = '1';
      if (immediate) {
        panel.style.maxHeight = 'none';
        return;
      }
      panel.style.maxHeight = `${Math.max(runtime.scrollHeight, 1)}px`;
      return;
    }

    if (immediate) {
      panel.style.maxHeight = '0px';
      panel.style.opacity = '0';
      return;
    }

    panel.style.maxHeight = `${Math.max(runtime.scrollHeight, 1)}px`;
    panel.offsetHeight;
    panel.style.maxHeight = '0px';
    panel.style.opacity = '0';
  }

  syncSveaPanelHeight(widgetRoot) {
    if (!widgetRoot || !widgetRoot.classList.contains('is-open')) return;
    const panel = this.getSveaPanelNode(widgetRoot);
    const runtime = this.getSveaRuntimeNode(widgetRoot);
    if (!panel || !runtime) return;
    panel.hidden = false;
    panel.style.maxHeight = `${Math.max(runtime.scrollHeight, 1)}px`;
    panel.style.opacity = '1';
  }

  dedupeSveaWidgetPanels(widgetRoot) {
    if (!widgetRoot) return;
    const runtime = this.getSveaRuntimeNode(widgetRoot) || widgetRoot;

    const headers = Array.from(runtime.querySelectorAll('.svea-pp-widget-header__value'));
    if (headers.length > 1) {
      const panelRoots = [];
      headers.forEach((header) => {
        let node = header;
        while (node && node.parentElement && node.parentElement !== runtime) {
          node = node.parentElement;
        }
        if (node && node.parentElement === runtime && !panelRoots.includes(node)) {
          panelRoots.push(node);
        }
      });
      if (panelRoots.length > 1) {
        panelRoots.slice(1).forEach((node) => node.remove());
      }
    }

    const candidates = Array.from(runtime.children).filter((node) => {
      if (!(node instanceof HTMLElement)) return false;
      if (node.matches && node.matches('script.svea-pp-widget-part-payment')) return false;
      const className = typeof node.className === 'string' ? node.className : '';
      return className.indexOf('svea-pp-widget') !== -1;
    });

    if (candidates.length > 1) {
      candidates.slice(1).forEach((node) => node.remove());
    }
  }

  bindSveaQuantityWatchers() {
    if (this.sveaQuantityWatchersBound === true) return;
    const scope = this.getSveaWidgetScope();
    if (!scope) return;

    const onQuantityChange = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.name !== 'quantity') return;
      if (!scope.contains(target)) return;
      this.updateSveaWidget();
    };

    scope.addEventListener('change', onQuantityChange, true);
    scope.addEventListener('input', onQuantityChange, true);
    this.sveaQuantityWatchersBound = true;
  }

  getSveaSummaryNode(widgetRoot) {
    if (!widgetRoot) return null;
    return widgetRoot.querySelector('[data-svea-summary-text]');
  }

  parseSveaNumber(value) {
    if (value === undefined || value === null) return null;
    const normalized = String(value)
      .replace(',', '.')
      .replace(/[^0-9.-]/g, '')
      .trim();
    if (!normalized.length) return null;
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  readSveaMonthlyPaymentText(widgetRoot) {
    if (!widgetRoot) return null;
    const runtime = this.getSveaRuntimeNode(widgetRoot) || widgetRoot;
    const wrap = runtime.querySelector('.svea-pp-widget-header__value');
    if (!wrap) return null;

    const items = Array.from(wrap.querySelectorAll('.svea-pp-widget-header-animate'));
    if (!items.length) {
      const text = String(wrap.textContent || '').replace(/\u00A0/g, ' ').replace(/\s+/g, ' ').trim();
      return text.length ? text : null;
    }

    const picked = items.reduce((best, node) => {
      const value = this.parseSveaNumber(node.getAttribute('data-svea-pp-widget-header-value'));
      const comparable = value === null ? Number.NEGATIVE_INFINITY : value;
      if (!best || comparable > best.comparable) {
        return { node, comparable };
      }
      return best;
    }, null);

    const sourceNode = picked?.node || items[0];
    const spanText = Array.from(sourceNode.querySelectorAll('span'))
      .map((span) => span.textContent || '')
      .join('')
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (spanText.length) return spanText;
    const text = String(sourceNode.textContent || '').replace(/\u00A0/g, ' ').replace(/\s+/g, ' ').trim();
    return text.length ? text : null;
  }

  formatSveaSummaryLabel(widgetRoot, paymentText) {
    const templateRaw = String(widgetRoot?.dataset?.sveaSummaryTemplate || 'Alkaen [payment]').trim();
    const template = templateRaw.length ? templateRaw : 'Alkaen [payment]';
    if (template.includes('[payment]')) {
      return template.replace('[payment]', paymentText);
    }
    return `${template} ${paymentText}`.trim();
  }

  setSveaSummaryText(widgetRoot, text) {
    const summaryNode = this.getSveaSummaryNode(widgetRoot);
    if (!summaryNode) return;
    const nextText = String(text || '');
    const hasContent = nextText.replace(/\u00A0/g, ' ').trim().length > 0;

    if (summaryNode.textContent === nextText) {
      if (!hasContent) {
        summaryNode.classList.remove('is-ready');
      }
      return;
    }

    summaryNode.textContent = nextText;

    if (!hasContent) {
      summaryNode.classList.remove('is-ready');
      return;
    }

    // Re-trigger the same reveal animation when payment text changes.
    summaryNode.classList.remove('is-ready');
    const animationToken = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    summaryNode.dataset.sveaSummaryAnimationToken = animationToken;
    const reveal = () => {
      if (summaryNode.dataset.sveaSummaryAnimationToken !== animationToken) return;
      summaryNode.classList.add('is-ready');
    };

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => window.requestAnimationFrame(reveal));
    } else {
      window.setTimeout(reveal, 16);
    }
  }

  updateSveaSummary(widgetRoot) {
    const summaryNode = this.getSveaSummaryNode(widgetRoot);
    if (!summaryNode) return;

    const fallbackText = widgetRoot.classList.contains('is-unavailable')
      ? (widgetRoot.dataset.sveaSummaryUnavailable || widgetRoot.dataset.sveaSummaryFallback || 'Tarkista osamaksun hinta')
      : (widgetRoot.dataset.sveaSummaryFallback || 'Tarkista osamaksun hinta');

    const paymentText = this.readSveaMonthlyPaymentText(widgetRoot);
    if (paymentText) {
      widgetRoot.dataset.sveaLastPaymentText = paymentText;
      this.setSveaSummaryText(widgetRoot, this.formatSveaSummaryLabel(widgetRoot, paymentText));
      return;
    }

    if (widgetRoot.classList.contains('is-unavailable')) {
      this.setSveaSummaryText(widgetRoot, fallbackText);
      return;
    }

    const lastPaymentText = String(widgetRoot.dataset.sveaLastPaymentText || '').trim();
    if (lastPaymentText.length) {
      this.setSveaSummaryText(widgetRoot, this.formatSveaSummaryLabel(widgetRoot, lastPaymentText));
      return;
    }

    // Avoid fallback flicker while widget markup is still being rebuilt.
    if (widgetRoot.classList.contains('is-loading')) {
      const currentText = String(summaryNode.textContent || '').trim();
      if (currentText.length) return;
      this.setSveaSummaryText(widgetRoot, '\u00A0');
      return;
    }

    this.setSveaSummaryText(widgetRoot, fallbackText);
  }

  clearSveaWidgetRuntime(widgetRoot) {
    const runtime = this.getSveaRuntimeNode(widgetRoot);
    if (!runtime) return;
    runtime.innerHTML = '';
  }

  setSveaWidgetUnavailable(widgetRoot, reason = 'unknown') {
    if (!widgetRoot) return;
    this.sveaLog('warn', 'widget.set-unavailable', { reason }, widgetRoot);
    this.clearSveaWidgetRuntime(widgetRoot);
    widgetRoot.classList.remove('is-loading');
    widgetRoot.classList.add('is-unavailable');
    this.setSveaWidgetOpen(widgetRoot, false, true);
    const toggle = this.getSveaToggleNode(widgetRoot);
    if (toggle) {
      toggle.disabled = true;
      toggle.setAttribute('aria-disabled', 'true');
    }
    this.updateSveaSummary(widgetRoot);
    this.logSveaStatus('widget.unavailable', { reason }, this.getSveaWidgetScope());
  }

  bindSveaWidgetInteractions() {
    const scope = this.getSveaWidgetScope();
    const widgets = this.getSveaWidgetRoots(scope);
    if (!widgets.length) return;

    widgets.forEach((widgetRoot) => {
      if (widgetRoot.dataset.sveaInteractionBound === 'true') {
        if (this.isSveaAlwaysOpen(widgetRoot)) {
          this.setSveaWidgetOpen(widgetRoot, true, true);
        }
        this.syncSveaPanelHeight(widgetRoot);
        return;
      }

      const toggle = this.getSveaToggleNode(widgetRoot);
      const panel = this.getSveaPanelNode(widgetRoot);
      if (!panel) {
        this.sveaLog('warn', 'widget.interactions.missing-panel', {}, widgetRoot);
        return;
      }

      if (!toggle) {
        widgetRoot.dataset.sveaInteractionBound = 'true';
        this.sveaLog('info', 'widget.interactions.no-toggle', {
          alwaysOpen: this.isSveaAlwaysOpen(widgetRoot)
        }, widgetRoot);
        if (this.isSveaAlwaysOpen(widgetRoot) && !widgetRoot.classList.contains('is-unavailable')) {
          widgetRoot.classList.add('is-open');
          panel.hidden = false;
          panel.style.maxHeight = 'none';
          panel.style.opacity = '1';
        }
        return;
      }

      widgetRoot.dataset.sveaInteractionBound = 'true';

      if (this.isSveaAlwaysOpen(widgetRoot)) {
        this.setSveaWidgetOpen(widgetRoot, true, true);
        this.syncSveaPanelHeight(widgetRoot);
        return;
      }

      toggle.addEventListener('click', () => {
        const shouldOpen = !widgetRoot.classList.contains('is-open');
        this.setSveaWidgetOpen(widgetRoot, shouldOpen);
      });

      panel.addEventListener('transitionend', (event) => {
        if (event.propertyName !== 'max-height') return;
        if (!widgetRoot.classList.contains('is-open')) return;
        this.syncSveaPanelHeight(widgetRoot);
      });

      this.setSveaWidgetOpen(widgetRoot, false, true);
    });
  }

  bindSveaWidgetGuards() {
    const scope = this.getSveaWidgetScope();
    const widgets = this.getSveaWidgetRoots(scope);
    if (!widgets.length) return;

    widgets.forEach((widgetRoot) => {
      if (widgetRoot.dataset.sveaGuardBound === 'true') {
        this.dedupeSveaWidgetPanels(widgetRoot);
        this.updateSveaSummary(widgetRoot);
        this.syncSveaPanelHeight(widgetRoot);
        return;
      }

      widgetRoot.dataset.sveaGuardBound = 'true';

      const observer = new MutationObserver(() => {
        this.dedupeSveaWidgetPanels(widgetRoot);
        this.updateSveaSummary(widgetRoot);
        this.syncSveaPanelHeight(widgetRoot);
      });
      observer.observe(widgetRoot, { childList: true, subtree: true });

      this.dedupeSveaWidgetPanels(widgetRoot);
      this.updateSveaSummary(widgetRoot);
      this.syncSveaPanelHeight(widgetRoot);
    });
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({ }, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #product-form-${this.dataset.section}-product-buy-bar`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      if (!input) return;
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  dispatchVariantChange() {
    if (!this.currentVariant) return;
    document.dispatchEvent(new CustomEvent('woolman:variant:change', {
      detail: {
        variant: this.currentVariant,
        sectionId: this.dataset.section || null
      }
    }));
  }

  async updateSveaWidget() {
    const scope = this.getSveaWidgetScope();
    this.bindSveaWidgetGuards();
    this.bindSveaWidgetInteractions();

    const widgets = this.getSveaWidgetRoots(scope).filter((node) => node.hasAttribute('data-svea-widget'));
    if (!widgets.length) {
      this.logSveaStatus('update.no-widgets', {}, scope);
      return;
    }

    if (!this.currentVariant) {
      this.logSveaStatus('update.no-current-variant', { widgetCount: widgets.length }, scope);
      widgets.forEach((widgetRoot) => this.setSveaWidgetUnavailable(widgetRoot, 'missing-current-variant'));
      return;
    }

    const getQuantityValue = () => {
      const formId = `product-form-${this.dataset.section}`;
      const form = document.getElementById(formId);
      const candidates = [
        form?.querySelector('input[name="quantity"]'),
        scope.querySelector(`input[name="quantity"][form="${formId}"]`),
        scope.querySelector(`#${formId} input[name="quantity"]`)
      ].filter(Boolean);
      const input = candidates[0] || null;
      if (!input) return 1;

      const fromValue = parseInt(String(input.value || '').trim(), 10);
      if (Number.isFinite(fromValue) && fromValue > 0) return fromValue;

      const fromMinMultiple = parseInt(String(input.dataset?.minMultiple || '').trim(), 10);
      if (Number.isFinite(fromMinMultiple) && fromMinMultiple > 0) return fromMinMultiple;

      const fromMinAttr = parseInt(String(input.getAttribute('min') || '').trim(), 10);
      if (Number.isFinite(fromMinAttr) && fromMinAttr > 0) return fromMinAttr;

      return 1;
    };

    const basePrice = Number(this.currentVariant.price || 0) / 100;
    const quantity = getQuantityValue();
    const price = basePrice * quantity;
    if (!Number.isFinite(price) || price <= 0) {
      this.logSveaStatus('update.invalid-price', {
        basePrice,
        quantity,
        computedPrice: price,
        widgetCount: widgets.length
      }, scope);
      widgets.forEach((widgetRoot) => this.setSveaWidgetUnavailable(widgetRoot, 'invalid-price'));
      return;
    }
    const priceText = (Math.round(price * 100) / 100).toFixed(2);
    this.sveaLog('info', 'update.price-resolved', {
      basePrice,
      quantity,
      price,
      priceText,
      widgetCount: widgets.length
    });

    const providerSrc = widgets
      .map((widgetRoot) => String(widgetRoot?.dataset?.sveaSrc || '').trim())
      .find((value) => value.length > 0)
      || this.getSveaProviderDefaultSrc();

    try {
      await this.ensureSveaProviderLoaded(providerSrc);
    } catch (error) {
      this.sveaLog('error', 'provider.load.error', {
        src: providerSrc,
        message: error?.message || String(error)
      });
      widgets.forEach((widgetRoot) => this.setSveaWidgetUnavailable(widgetRoot, 'provider-load-failed'));
      return;
    }

    const normalizeDatasetValue = (value) => {
      if (value === undefined || value === null) return null;
      const text = String(value).trim();
      return text.length ? text : null;
    };
    const buildConfigSignature = (config) => {
      return [
        config.src || '',
        config.sellerid || '',
        config.host || '',
        config.thresholdPrices || '',
        config.activeColor || '',
        config.textColor || '',
        config.highlightColor || '',
        config.borderColor || '',
        config.borderRadius || '',
        config.locale || '',
        config.layout || ''
      ].join('|');
    };
    const applyScriptAttributes = (scriptNode, config) => {
      if (!scriptNode) return;
      scriptNode.className = 'svea-pp-widget-part-payment';
      scriptNode.setAttribute('data-price', priceText);

      const attrPairs = [
        ['data-sellerid', config.sellerid],
        ['data-maksuturva-host', config.host],
        ['data-threshold-prices', config.thresholdPrices],
        ['data-active-color', config.activeColor],
        ['data-text-color', config.textColor],
        ['data-highlight-color', config.highlightColor],
        ['data-border-color', config.borderColor],
        ['data-border-radius', config.borderRadius],
        ['data-locale', config.locale],
        ['data-layout', config.layout]
      ];

      attrPairs.forEach(([name, value]) => {
        if (value) {
          scriptNode.setAttribute(name, value);
        } else {
          scriptNode.removeAttribute(name);
        }
      });
      scriptNode.removeAttribute('src');
    };

    const hasRenderedWidgetMarkup = (runtime) => {
      if (!runtime) return false;
      return Array.from(runtime.children).some((node) => {
        if (!(node instanceof HTMLElement)) return false;
        if (node.matches && node.matches('script.svea-pp-widget-part-payment')) return false;
        const className = typeof node.className === 'string' ? node.className : '';
        return className.indexOf('svea-pp-widget') !== -1;
      });
    };

    let initializeRequired = false;
    widgets.forEach((widgetRoot, index) => {
      try {
        this.sveaLog('info', 'widget.update.begin', { index }, widgetRoot);
        this.dedupeSveaWidgetPanels(widgetRoot);
        const runtime = this.getSveaRuntimeNode(widgetRoot);
        let currentScript = runtime?.querySelector('.svea-pp-widget-part-payment');
        const dataset = widgetRoot.dataset || {};
        if (!runtime) {
          this.sveaLog('error', 'widget.update.missing-runtime', { index }, widgetRoot);
          this.setSveaWidgetUnavailable(widgetRoot, 'missing-runtime-node');
          return;
        }

        const config = {
          src: normalizeDatasetValue(dataset.sveaSrc) || this.getSveaProviderDefaultSrc(),
          sellerid: normalizeDatasetValue(dataset.sveaSellerid) || currentScript?.getAttribute('data-sellerid') || null,
          host: normalizeDatasetValue(dataset.sveaHost) || currentScript?.getAttribute('data-maksuturva-host') || null,
          thresholdPrices: normalizeDatasetValue(dataset.sveaThresholdPrices) || currentScript?.getAttribute('data-threshold-prices') || null,
          activeColor: normalizeDatasetValue(dataset.sveaActiveColor) || currentScript?.getAttribute('data-active-color') || null,
          textColor: normalizeDatasetValue(dataset.sveaTextColor) || currentScript?.getAttribute('data-text-color') || null,
          highlightColor: normalizeDatasetValue(dataset.sveaHighlightColor) || currentScript?.getAttribute('data-highlight-color') || null,
          borderColor: normalizeDatasetValue(dataset.sveaBorderColor) || currentScript?.getAttribute('data-border-color') || null,
          borderRadius: normalizeDatasetValue(dataset.sveaBorderRadius) || currentScript?.getAttribute('data-border-radius') || null,
          locale: normalizeDatasetValue(dataset.sveaLocale) || currentScript?.getAttribute('data-locale') || null,
          layout: normalizeDatasetValue(dataset.sveaLayout) || currentScript?.getAttribute('data-layout') || null
        };

        if (!config.sellerid) {
          this.sveaLog('error', 'widget.update.missing-sellerid', {
            index,
            hasSource: !!config.src
          }, widgetRoot);
          this.setSveaWidgetUnavailable(widgetRoot, 'missing-sellerid');
          return;
        }

        widgetRoot.classList.remove('is-unavailable');
        widgetRoot.classList.add('is-loading');
        const toggle = this.getSveaToggleNode(widgetRoot);
        if (toggle) {
          toggle.disabled = false;
          toggle.removeAttribute('aria-disabled');
        }
        if (this.isSveaAlwaysOpen(widgetRoot)) {
          this.setSveaWidgetOpen(widgetRoot, true, true);
        }

        const renderToken = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        widgetRoot.dataset.sveaRenderToken = renderToken;
        const nextConfigSignature = buildConfigSignature(config);
        const previousConfigSignature = widgetRoot.dataset.sveaConfigSignature || nextConfigSignature;
        widgetRoot.dataset.sveaConfigSignature = nextConfigSignature;
        const syncWidgetState = () => {
          if (widgetRoot.dataset.sveaRenderToken !== renderToken) return;
          this.dedupeSveaWidgetPanels(widgetRoot);
          this.updateSveaSummary(widgetRoot);
          this.syncSveaPanelHeight(widgetRoot);
        };
        const finishLoading = (delay = 1200) => {
          window.setTimeout(() => {
            if (widgetRoot.dataset.sveaRenderToken !== renderToken) return;
            widgetRoot.classList.remove('is-loading');
            syncWidgetState();
            this.sveaLog('info', 'widget.loading.finished', { index, delay }, widgetRoot);
          }, delay);
        };

        const hadRenderedMarkup = hasRenderedWidgetMarkup(runtime);
        if (!currentScript) {
          currentScript = document.createElement('script');
          runtime.appendChild(currentScript);
          this.sveaLog('info', 'widget.script.placeholder-created', { index }, widgetRoot);
        }
        applyScriptAttributes(currentScript, config);

        const shouldInitialize = !hadRenderedMarkup || previousConfigSignature !== nextConfigSignature;
        this.sveaLog('info', shouldInitialize ? 'widget.render.initialize' : 'widget.render.refresh', {
          index,
          hasCurrentScript: !!currentScript,
          hasRenderedMarkup: hadRenderedMarkup,
          configChanged: previousConfigSignature !== nextConfigSignature
        }, widgetRoot);

        if (shouldInitialize) initializeRequired = true;

        window.setTimeout(syncWidgetState, 60);
        window.setTimeout(syncWidgetState, 180);
        window.setTimeout(syncWidgetState, 420);
        finishLoading(shouldInitialize ? 1600 : 900);
      } catch (error) {
        this.sveaLog('error', 'widget.update.exception', {
          index,
          message: error?.message || String(error)
        }, widgetRoot);
        this.setSveaWidgetUnavailable(widgetRoot, 'exception');
      }
    });

    const refreshMode = initializeRequired ? 'initialize' : 'update';
    const refreshOk = this.refreshSveaProvider(priceText, null, refreshMode);
    if (!refreshOk) {
      widgets.forEach((widgetRoot) => this.setSveaWidgetUnavailable(widgetRoot, 'provider-not-ready'));
      return;
    }

    window.setTimeout(() => {
      widgets.forEach((widgetRoot) => {
        this.dedupeSveaWidgetPanels(widgetRoot);
        this.updateSveaSummary(widgetRoot);
        this.syncSveaPanelHeight(widgetRoot);
      });
      this.logSveaStatus('update.complete', { widgetCount: widgets.length }, scope);
    }, 120);
  }

  updateVariantStatuses() {
    if (!this.variantData) return false;
    const option1Value = (this.options && this.options.length ? this.options[0] : null)
      || this.querySelector('input:checked, option:checked')?.value;
    if (!option1Value) return false;

    const selectedOptionOneVariants = this.variantData.filter((variant) => option1Value === variant.option1);
    const inputWrappers = [...this.querySelectorAll('.product-form__input')];
    for (let index = 1; index < inputWrappers.length; index += 1) {
      const option = inputWrappers[index];
      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
      if (!optionInputs.length) continue;

      const previousSelected = inputWrappers[index - 1].querySelector('input:checked, option:checked');
      const previousOptionSelected = previousSelected?.value;
      if (!previousOptionSelected) continue;

      const availableOptionInputsValue = selectedOptionOneVariants
        .filter((variant) => variant.available && variant[`option${ index }`] === previousOptionSelected)
        .map((variantOption) => variantOption[`option${ index + 1 }`]);

      this.setInputAvailability(optionInputs, availableOptionInputsValue);

      const currentSelected = option.querySelector('input:checked, option:checked');
      const currentValue = currentSelected?.value;
      if (currentSelected && currentValue && !availableOptionInputsValue.includes(currentValue)) {
        const fallback = this.pickFallbackOption(optionInputs, availableOptionInputsValue);
        if (fallback) {
          this.applyFallbackSelection(fallback);
          return true;
        }
      }
    }
    return false;
  }

  pickFallbackOption(optionInputs, availableValues) {
    if (!availableValues || availableValues.length === 0) return null;
    const uniqueValues = Array.from(new Set(availableValues));
    const preferredValue = uniqueValues.find((value) => {
      const text = String(value || '');
      return /(^|\s)ilman(\s|$)/i.test(text)
        || /\bnone\b/i.test(text)
        || /\bwithout\b/i.test(text)
        || /\bno\b/i.test(text)
        || /(^|\s)ei(\s|$)/i.test(text);
    });
    const targetValue = preferredValue || uniqueValues[0];
    return optionInputs.find((input) => input.getAttribute('value') === targetValue);
  }

  applyFallbackSelection(input) {
    if (!input) return;
    if (input.tagName === 'OPTION') {
      input.selected = true;
      const select = input.closest('select');
      if (select) {
        select.value = input.value;
      }
    } else {
      input.checked = true;
    }
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  getUnavailableMessageText() {
    if (this.dataset.unavailableMessage) return this.dataset.unavailableMessage;
    if (window.variantStrings && window.variantStrings.unavailableVariant) return window.variantStrings.unavailableVariant;
    if (window.variantStrings && window.variantStrings.unavailable) return window.variantStrings.unavailable;
    return 'Tätä varianttia ei ole saatavilla';
  }

  showUnavailableMessage() {
    const message = this.querySelector('[data-variant-unavailable-message]');
    if (!message) return;
    message.textContent = this.getUnavailableMessageText();
    message.hidden = false;
    message.classList.add('is-visible');
    if (this.unavailableMessageTimer) {
      window.clearTimeout(this.unavailableMessageTimer);
    }
    this.unavailableMessageTimer = window.setTimeout(() => {
      this.hideUnavailableMessage();
    }, 2500);
  }

  hideUnavailableMessage() {
    const message = this.querySelector('[data-variant-unavailable-message]');
    if (!message) return;
    message.classList.remove('is-visible');
    message.hidden = true;
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    const availableSet = new Set(listOfAvailableOptions || []);
    listOfOptions.forEach((input) => {
      const value = input.getAttribute('value');
      const isAvailable = availableSet.has(value);
      if (input.tagName === 'OPTION') {
        input.disabled = !isAvailable;
        input.dataset.unavailable = isAvailable ? 'false' : 'true';
        input.textContent = isAvailable
          ? value
          : window.variantStrings.unavailable_with_option.replace('[value]', value);
      } else {
        input.classList.toggle('disabled', !isAvailable);
        input.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
        input.dataset.unavailable = isAvailable ? 'false' : 'true';
      }
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  updateInventoryStatus() {
    this.productInventoryNode = document.querySelector('[data-product-inventory]');

    if (!this.productInventoryNode || !window.inventories || !window.inventories[this.dataset.productId]) return;

    this.inventoryThreshold = parseInt(this.productInventoryNode.dataset.threshold);
    this.showQuantityInStock = this.productInventoryNode.dataset.showQuantityInStock;

    const variantInventoryObject = window.inventories[this.dataset.productId][this.currentVariant?.id];

    if (!this.currentVariant || !this.currentVariant.inventory_management) {
      this.toggleInventoryQuantity(false);
      this.toggleIncomingInventory(false);

      return;
    }
    if (!variantInventoryObject) {
      this.toggleInventoryQuantity(false);
      this.toggleIncomingInventory(false);
      return;
    }

    if (this.currentVariant.inventory_management === 'shopify') {
      if (variantInventoryObject.quantity <= 0 && variantInventoryObject.incoming === 'true' && variantInventoryObject.quantity < this.inventoryThreshold) {
        this.toggleInventoryQuantity(false);
        this.toggleIncomingInventory(true, variantInventoryObject.next_incoming_date);
      } else {
        this.toggleInventoryQuantity(true, variantInventoryObject.quantity);
        this.toggleIncomingInventory(false);
      }
    }
  }

  toggleInventoryQuantity(show, quantity) {
    if (show === false) {
      this.productInventoryNode.classList.add('hide');
      return;
    }

    this.productInventoryNode.classList.remove('inventory-status--in-stock', 'inventory-status--low', 'inventory-status--sold-out', 'inventory-status--sold-out-continue-selling');

    if (this.currentVariant && this.currentVariant.available && parseInt(quantity) <= parseInt(this.inventoryThreshold) && parseInt(quantity) > 0) {
      this.productInventoryNode.classList.add('inventory-status--low');
      if (this.showQuantityInStock === 'true') {
        this.productInventoryNode.firstElementChild.textContent = window.variantStrings.inventory.few_left_with_quantity.replace('[quantity]', quantity);
      } else {
        this.productInventoryNode.firstElementChild.textContent = window.variantStrings.inventory.few_left;
      }
    } else if (this.currentVariant && this.currentVariant.available && parseInt(quantity) > parseInt(this.inventoryThreshold)) {
      this.productInventoryNode.classList.add('inventory-status--in-stock');
      if (this.showQuantityInStock === 'true') {
        this.productInventoryNode.firstElementChild.textContent = window.variantStrings.inventory.in_stock_with_quantity.replace('[quantity]', quantity);
      } else {
        this.productInventoryNode.firstElementChild.textContent = window.inventories[this.dataset.productId][this.currentVariant.id].estimated_delivery_time
      }
    } else if (this.currentVariant && this.currentVariant.available && parseInt(quantity) < 1) {
      this.productInventoryNode.classList.add('inventory-status--sold-out-continue-selling');
      this.productInventoryNode.firstElementChild.textContent = window.inventories[this.dataset.productId][this.currentVariant.id].estimated_delivery_time
    } else if (this.currentVariant && this.currentVariant.available) {
      this.productInventoryNode.classList.add('inventory-status--in-stock');
      this.productInventoryNode.firstElementChild.textContent = window.inventories[this.dataset.productId][this.currentVariant.id].estimated_delivery_time
    } else {
      this.productInventoryNode.classList.add('inventory-status--sold-out');
      this.productInventoryNode.firstElementChild.textContent = window.variantStrings.inventory.out_of_stock;
    }

    this.productInventoryNode.classList.remove('hide');
  }

  toggleIncomingInventory(show, date) {
    const incomingInventoryNode = document.querySelector('[data-incoming-inventory]');
    if (!incomingInventoryNode) return;

    if (show == false && incomingInventoryNode) {
      incomingInventoryNode.classList.add('hide');

      return;
    }

    let string = !date ?
      window.variantStrings.inventory.waiting_for_stock :
      window.variantStrings.inventory.will_be_in_stock_after.replace('[date]', date);

    incomingInventoryNode.classList.remove('hide');
    incomingInventoryNode.firstElementChild.textContent = string;
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(`price-${this.dataset.section}`);
        const skuSource = html.getElementById(
          `Sku-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
        const destinationBuyBarInfo = document.getElementById(
          `buy-bar-info-${this.dataset.section}`
        );
        const source = html.getElementById(`price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        const buyBarInfoSource = html.getElementById(`buy-bar-info-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`);
        if (source && destination) destination.innerHTML = source.innerHTML;
        if (source && destinationBuyBarInfo) destinationBuyBarInfo.innerHTML = buyBarInfoSource.innerHTML;

        if (skuSource && skuDestination) {
          skuDestination.innerHTML = skuSource.innerHTML;
          skuDestination.classList.toggle('hidden', skuSource.classList.contains('hidden'));
        }

        const price = document.getElementById(`price-${this.dataset.section}`);

        if (price) price.classList.remove('hidden');
        this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    const multiplyButtons = productForm.querySelectorAll('.btn--add-multiplier');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
      if (multiplyButtons) {
        multiplyButtons.forEach(button => {
          button.disabled = true;
        });
      }
    } else {
      addButton.removeAttribute('disabled');
      if (this.dataset.isPreorder == 'true') {
        addButtonText.textContent = window.variantStrings.preOrder;
      } else {
        addButtonText.textContent = window.variantStrings.addToCart;
      }
      if (multiplyButtons) {
        multiplyButtons.forEach(button => {
          button.removeAttribute('disabled');
        });
      }
    }

    const productForm_BuyBar = document.getElementById(
      `product-form-${this.dataset.section}-product-buy-bar`
    );
    if (productForm_BuyBar) {
      const addButton_BuyBar = productForm_BuyBar.querySelector('[name="add"]');
      const addButtonText_BuyBar = productForm_BuyBar.querySelector(
        '[name="add"] > span'
      );

      if (disable) {
        addButton_BuyBar.setAttribute("disabled", "disabled");
        if (text) addButtonText_BuyBar.textContent = text;
      } else {
        addButton_BuyBar.removeAttribute("disabled");
        if (this.dataset.isPreorder == "true") {
          addButtonText_BuyBar.textContent = window.variantStrings.preOrder;
        } else {
          addButtonText_BuyBar.textContent = window.variantStrings.addToCart;
        }
      }
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const scope = this.getSveaWidgetScope();
    const widgets = this.getSveaWidgetRoots(scope).filter((node) => node.hasAttribute('data-svea-widget'));
    widgets.forEach((widgetRoot) => this.setSveaWidgetUnavailable(widgetRoot));

    const button = document.getElementById(`product-form-${this.dataset.section}`);
    if (!button) return;
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('hidden');

    const button_BuyBar = document.getElementById(
      `product-form-${this.dataset.section}-product-buy-bar`
    );
    if (!button_BuyBar) return;
    const addButton_BuyBar = button_BuyBar.querySelector('[name="add"]');
    const addButtonText_BuyBar = button_BuyBar.querySelector(
      '[name="add"] > span'
    );
    const price_BuyBar = document.getElementById(
      `price-${this.dataset.section}-product-buy-bar`
    );
    if (!addButton_BuyBar) return;
    addButtonText_BuyBar.textContent = window.variantStrings.unavailable;
    if (price_BuyBar) price_BuyBar.classList.add("hidden");
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
    this.addEventListener('click', this.onOptionClick);
    this.addEventListener('keydown', this.onOptionKeydown);
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    const availableSet = new Set(listOfAvailableOptions || []);
    listOfOptions.forEach((input) => {
      const value = input.getAttribute('value');
      const isAvailable = availableSet.has(value);
      input.classList.toggle('disabled', !isAvailable);
      input.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
      input.dataset.unavailable = isAvailable ? 'false' : 'true';
    });
  }

  onOptionClick(event) {
    const target = event.target;
    let input = null;
    if (target.matches && target.matches('input[type="radio"]')) {
      input = target;
    } else {
      const label = target.closest ? target.closest('label') : null;
      if (label) {
        input = label.control || document.getElementById(label.getAttribute('for'));
      }
    }
    if (!input || !input.classList.contains('disabled')) return;
    event.preventDefault();
    event.stopPropagation();
    this.showUnavailableMessage();
  }

  onOptionKeydown(event) {
    if (event.key !== ' ' && event.key !== 'Enter') return;
    const input = event.target?.closest ? event.target.closest('input[type="radio"]') : null;
    if (!input || !input.classList.contains('disabled')) return;
    event.preventDefault();
    event.stopPropagation();
    this.showUnavailableMessage();
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      const radios = Array.from(fieldset.querySelectorAll('input'));
      const selected = radios.find((radio) => radio.checked) || radios[0];
      return selected ? selected.value : '';
    });

    this.updateLegends();
  }

  updateLegends() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    fieldsets.forEach((fieldset) => {
      const legend = fieldset.querySelector('legend [data-variant-legend]');
      const selectedOption = Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked);
      if (!legend || !selectedOption) return;
      legend.textContent = selectedOption.getAttribute('value');
    });
  }
}

customElements.define('variant-radios', VariantRadios);

class ProductBuyBar extends HTMLElement {
  constructor() {
    "use strict";
    super();

    let classes = { show: "show" };
    let mainProductForm = document.querySelector(".product-form");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // In view -> Hide bar
            this.classList.remove(classes.show);
          } else if (window.scrollY > 200) {
            // Out of view AND scrolled a little from top -> Show bar
            this.classList.add(classes.show);
          }
        });
      },
      { threshold: 0.25 }
    );

    observer.observe(mainProductForm);

    // This script is loaded in product-template.liquid --> Footer observer after DOM ready
    document.addEventListener("DOMContentLoaded", function () {
      let siteFooter = document.querySelector("footer.footer");
      if (!siteFooter) return;
      observer.observe(siteFooter);
    });
  }
}
customElements.define("product-buy-bar", ProductBuyBar);

class UpsellCard extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('select');
    this.image = this.querySelector('.card-media img');

    if (this.select) {
      this.select.addEventListener('change', (event) => {
        const selectedOption = event.target.options[event.target.selectedIndex];
        const newImgSrc = selectedOption.getAttribute('data-variant-img-src');
        if (newImgSrc) { this.updateImage(newImgSrc) }
      });
    }
  }

  updateImage(newImgSrc) {
    if (this.image) {
      this.image.src = newImgSrc;
    }
  }
}

customElements.define('upsell-card', UpsellCard);

(function initQuantityMultiplesGlobal() {
  if (window.__QuantityMultiplesInit) return;
  window.__QuantityMultiplesInit = true;

  function toInt(v, fallback = 0) {
    const n = parseInt(String(v ?? '').trim(), 10);
    return Number.isFinite(n) ? n : fallback;
  }

  function getStep(input) {
    const stepAttr = input?.getAttribute?.('step');
    const dataAttr = input?.dataset?.minMultiple;
    const step = toInt(stepAttr || dataAttr, 1);
    return step > 0 ? step : 1;
  }

  function isCartContext(input) {
    if (!input) return false;
    const name = String(input.getAttribute('name') || '');
    if (name.includes('updates')) return true;
    if (input.dataset && input.dataset.index) return true;
    if (input.closest('#cart')) return true;
    if (input.closest('cart-items')) return true;
    if (input.closest('[data-drawer]')) return true;
    return false;
  }

  // Palauttaa min-arvon: cartissa min voi olla 0 (poisto), muuten min >= step
  function getMin(input, step) {
    const minAttrRaw = input?.getAttribute?.('min');
    const minAttr = minAttrRaw === null ? null : toInt(minAttrRaw, 0);

    const cart = isCartContext(input);

    // Cartissa kun min="0", 0 sallitaan aidosti
    if (cart && minAttr === 0) return 0;

    // Muuten min vähintään step
    const min = (minAttr === null) ? step : minAttr;
    return min > 0 ? Math.max(min, step) : step;
  }

  // Pyöristetään ylöspäin kerrannaiseksi. Cartissa 0 säilytetään 0:na.
  function snap(value, step, min, allowZero) {
    const raw = toInt(value, 0);

    if (allowZero && raw <= 0) return 0;

    const minPos = (min === 0) ? step : Math.max(min, step);
    const v = Math.max(raw, minPos);
    const snapped = Math.ceil(v / step) * step;
    return Math.max(snapped, minPos);
  }

  function sanitizeInput(input) {
    if (!(input instanceof HTMLInputElement)) return;
    if (input.type !== 'number') return;
    if (!input.closest('quantity-input')) return;

    const step = getStep(input);
    if (step <= 1) return;

    const min = getMin(input, step);
    const allowZero = (min === 0) && isCartContext(input);

    const next = snap(input.value, step, min, allowZero);
    const nextStr = String(next);
    if (input.value !== nextStr) input.value = nextStr;
  }

  function handleQuantityInputChangeOrBlur(e) {
    sanitizeInput(e.target);
  }

  function handleFormSubmit(e) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;

    // Siivoa kaikki quantity-input kentät lomakkeessa
    form.querySelectorAll('quantity-input input[type="number"]').forEach((input) => {
      sanitizeInput(input);
    });
  }

  // HUOM: ei kaapata clickkejä ollenkaan -> teidän QuantityInput custom element hoitaa stepUp/stepDown.
  document.addEventListener('change', handleQuantityInputChangeOrBlur, true);
  document.addEventListener('blur', handleQuantityInputChangeOrBlur, true);
  document.addEventListener('submit', handleFormSubmit, true);
})();
